import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Divider,
  Stack,
  IconButton,
} from "@mui/material";
import AddIcon    from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useState } from "react";

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#449095" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#449095" },
};

const METHODS    = ["POST", "PUT"];
const AUTH_TYPES = ["None", "Bearer Token", "Basic Auth", "API Key"];

const INITIAL = {
  enabled:       false,
  url:           "",
  method:        "POST",
  auth_type:     "None",
  bearer_token:  "",
  basic_user:    "",
  basic_pass:    "",
  apikey_header: "X-API-Key",
  apikey_value:  "",
  signing_secret:"",
  retry:         true,
  headers:       [],
};

const WebhookConfig = () => {
  const [cfg, setCfg] = useState(INITIAL);

  const set = (path, value) =>
    setCfg((prev) => {
      const keys = path.split(".");
      if (keys.length === 1) return { ...prev, [path]: value };
      return { ...prev, [keys[0]]: { ...prev[keys[0]], [keys[1]]: value } };
    });

  const addHeader = () =>
    setCfg((prev) => ({ ...prev, headers: [...prev.headers, { key: "", value: "" }] }));

  const removeHeader = (i) =>
    setCfg((prev) => ({ ...prev, headers: prev.headers.filter((_, j) => j !== i) }));

  const updateHeader = (i, field, val) =>
    setCfg((prev) => {
      const headers = [...prev.headers];
      headers[i] = { ...headers[i], [field]: val };
      return { ...prev, headers };
    });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

      {/* ── Enable toggle ─────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontWeight: 600, color: "#e8e8e8" }}>Webhook</Typography>
          <Typography variant="caption" color="text.secondary">
            Send an HTTP request to an external endpoint on security events.
          </Typography>
        </Box>
        <FormControlLabel
          control={<Switch checked={cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} sx={switchSx} />}
          label={cfg.enabled ? "Enabled" : "Disabled"}
          labelPlacement="start"
        />
      </Box>

      <Divider />

      {/* ── Endpoint ──────────────────────────────────────────── */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
          Endpoint
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            select size="small" label="Method"
            value={cfg.method} onChange={(e) => set("method", e.target.value)}
            sx={{ minWidth: 100 }}
          >
            {METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          <TextField
            size="small" label="URL" fullWidth required
            placeholder="https://hooks.example.com/incident"
            value={cfg.url}
            onChange={(e) => set("url", e.target.value)}
          />
        </Stack>
      </Box>

      {/* ── Authentication ────────────────────────────────────── */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
          Authentication
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField
            select size="small" label="Type"
            value={cfg.auth_type} onChange={(e) => set("auth_type", e.target.value)}
            sx={{ minWidth: 160 }}
          >
            {AUTH_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>

          {cfg.auth_type === "Bearer Token" && (
            <TextField
              size="small" label="Token" type="password" sx={{ flex: 1, minWidth: 220 }}
              value={cfg.bearer_token} onChange={(e) => set("bearer_token", e.target.value)}
            />
          )}
          {cfg.auth_type === "Basic Auth" && (
            <>
              <TextField
                size="small" label="Username" sx={{ flex: 1, minWidth: 160 }}
                value={cfg.basic_user} onChange={(e) => set("basic_user", e.target.value)}
              />
              <TextField
                size="small" label="Password" type="password" sx={{ flex: 1, minWidth: 160 }}
                value={cfg.basic_pass} onChange={(e) => set("basic_pass", e.target.value)}
              />
            </>
          )}
          {cfg.auth_type === "API Key" && (
            <>
              <TextField
                size="small" label="Header name" sx={{ flex: 1, minWidth: 160 }}
                value={cfg.apikey_header} onChange={(e) => set("apikey_header", e.target.value)}
              />
              <TextField
                size="small" label="Value" type="password" sx={{ flex: 1, minWidth: 160 }}
                value={cfg.apikey_value} onChange={(e) => set("apikey_value", e.target.value)}
              />
            </>
          )}
        </Stack>
      </Box>

      {/* ── Custom headers ────────────────────────────────────── */}
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle2" sx={{ color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
            Custom Headers
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addHeader}
            sx={{ fontSize: "0.72rem", color: "#449095", textTransform: "none", minWidth: 0, p: "2px 8px" }}>
            Add
          </Button>
        </Box>
        {cfg.headers.length === 0 && (
          <Typography sx={{ fontSize: "0.78rem", color: "#555" }}>No custom headers.</Typography>
        )}
        <Stack spacing={1}>
          {cfg.headers.map((h, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <TextField
                size="small" label="Header name" sx={{ flex: 1 }}
                value={h.key} onChange={(e) => updateHeader(i, "key", e.target.value)}
                placeholder="X-Custom-Header"
              />
              <TextField
                size="small" label="Value" sx={{ flex: 2 }}
                value={h.value} onChange={(e) => updateHeader(i, "value", e.target.value)}
              />
              <IconButton size="small" onClick={() => removeHeader(i)} sx={{ color: "#666", "&:hover": { color: "#c0392b" } }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* ── Security ──────────────────────────────────────────── */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
          Security
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="flex-start">
          <TextField
            size="small" label="Signing secret" type="password"
            sx={{ flex: 1 }} value={cfg.signing_secret}
            onChange={(e) => set("signing_secret", e.target.value)}
            helperText="Used to sign payloads via HMAC-SHA256 (X-Hub-Signature-256)"
          />
          <FormControlLabel
            control={<Switch checked={cfg.retry} onChange={(e) => set("retry", e.target.checked)} sx={switchSx} />}
            label="Retry on failure"
            sx={{ mt: 0.5 }}
          />
        </Stack>
      </Box>

    </Box>
  );
};

export default WebhookConfig;
